// Devices (layout from design/isomorphic/src/pages/devices.tsx): this browser's device
// identity, devices waiting for its approval, the account key, and every device of the owner.
import { DeviceMobile } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import { AccountKeyCard } from "../account/AccountKeyCard";
import { accountRole } from "../account/setup";
import { accountStatusQuery, verifiedRecipients } from "../account/summary";
import { devicesQuery } from "../api/queries";
import { useDevice } from "../device/context";
import { DevicesTable } from "../device/DevicesTable";
import { PendingDevices } from "../device/PendingDevices";
import { ThisBrowserCard } from "../device/ThisBrowserCard";
import { useT } from "../i18n";
import { useProto } from "../device/useProto";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { WidgetCard } from "../ui/Card";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";

export default function DevicesPage() {
  const t = useT().devices.page;
  const query = useQuery(devicesQuery());
  const account = useQuery(accountStatusQuery());
  const device = useDevice();
  const proto = useProto();
  const linked = device.kind === "linked" ? device : undefined;
  const self = linked?.device.deviceId ?? (device.kind === "unlinked" ? device.pending?.device.deviceId : undefined);

  return (
    <>
      <PageHeader
        title={SECTIONS.devices.title}
        breadcrumb={sectionCrumbs("devices")}
        description={t.description}
      />
      <div className="grid gap-5 xl:grid-cols-12">
        <div className="flex flex-col gap-5 xl:col-span-7">
          <ThisBrowserCard devices={query.data} />
          {linked ? <PendingDevices proto={linked.proto} device={linked.device} /> : null}
        </div>
        <AccountKeyCard className="self-start xl:col-span-5" proto={proto} deviceId={linked?.device.deviceId} />
        <WidgetCard className="!p-3 lg:!p-4 xl:col-span-12" label={t.all} title={<span className="px-1">{t.all}</span>}>
          {query.isPending ? <Loading /> : null}
          {query.isError ? <ErrorState title={t.loadFailed} message={query.error.message} /> : null}
          {query.data?.length === 0 ? (
            <EmptyState compact icon={<DeviceMobile weight="duotone" />} title={t.none}>
              {t.noneHint}
            </EmptyState>
          ) : null}
          {query.data && query.data.length > 0 ? (
            <DevicesTable
              devices={query.data}
              marks={{
                proto,
                self,
                selfHolds: !!account.data && accountRole(account.data) === "holder",
                recipients: proto && account.data ? verifiedRecipients(proto, account.data) : undefined,
              }}
            />
          ) : null}
        </WidgetCard>
      </div>
    </>
  );
}
